/**
 * Customer catalogue modes — per-tier `muted` / `acceptEnquiries` on products_{key}.
 *
 * live     — unmuted. Add to cart. Price is required for selling.
 * hidden   — muted, no enquiries. Stays in Product Management only.
 * enquiry  — muted + acceptEnquiries. Visible with Enquire. Price optional and never shown.
 *
 * Enquiry visibility is not gated on Price, stock, or sale flags.
 * Separate from stock: out of stock live SKUs may still appear; muted without enquiries does not.
 *
 * **Box / hamper SKUs (live only):** shown on Home, Product Catalog, Product Detail, and
 * Admin place-on-behalf only when Stock is available (`x` unlimited or a number > 0).
 * Blank stock or 0 hides the box until staff set availability on that pricing sheet.
 */

/** Pure box detection — no Firebase / unifiedProductService (safe for catalogue load paths). */
export function isCustomerCatalogBoxProduct(product) {
    if (!product || typeof product !== 'object') return false;
    if (product.isBox === true || product.IsBox === true) return true;
    const sku = String(product.Code ?? product.code ?? product.sku ?? '').trim();
    if (!sku) return false;
    return /^BX-/i.test(sku) || /-BOX$/i.test(sku);
}

/**
 * Box sellability from the pricing-sheet Stock field.
 * Available: `x` / unlimited, or a finite number > 0.
 * Not available: blank, unset, 0, or non-numeric junk.
 */
export function isBoxStockAvailableForCustomer(product) {
    if (!product || typeof product !== 'object') return false;
    const raw = product.Stock ?? product.stock;
    if (raw === 'x' || raw === 'X') return true;
    if (typeof raw === 'string' && raw.trim().toLowerCase() === 'x') return true;
    if (raw === '' || raw == null) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0;
}

export function normalizeMutedFlag(raw) {
    if (raw === true || raw === false) return raw;
    const s = String(raw ?? '').trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
    return Boolean(raw);
}

/** Same coercion as muted — used for `acceptEnquiries`. */
export function normalizeAcceptEnquiriesFlag(raw) {
    return normalizeMutedFlag(raw);
}

/** @returns {boolean} true when the product is marked muted on this pricing list */
export function isProductMuted(product) {
    if (!product || typeof product !== 'object') return false;
    return normalizeMutedFlag(product.muted) === true;
}

/**
 * True when the product accepts customer enquiries.
 * Enquiries are only valid on muted products — non-muted + acceptEnquiries is ignored.
 */
export function productAcceptsEnquiries(product) {
    if (!product || typeof product !== 'object') return false;
    return isProductMuted(product) && normalizeAcceptEnquiriesFlag(product.acceptEnquiries) === true;
}

/** Alias: muted + acceptEnquiries (customer sees Enquire, not Add to cart). */
export function isEnquiryOnlyProduct(product) {
    return productAcceptsEnquiries(product);
}

export const CUSTOMER_CATALOG_MODE = Object.freeze({
    LIVE: 'live',
    HIDDEN: 'hidden',
    ENQUIRY: 'enquiry',
});

/**
 * Exactly one customer mode. Price / stock are not inputs (except live boxes — see isCatalogVisibleToCustomer).
 * @returns {'live'|'hidden'|'enquiry'}
 */
export function resolveCustomerCatalogMode(product) {
    if (!product || typeof product !== 'object') return CUSTOMER_CATALOG_MODE.HIDDEN;
    if (isEnquiryOnlyProduct(product)) return CUSTOMER_CATALOG_MODE.ENQUIRY;
    if (isProductMuted(product)) return CUSTOMER_CATALOG_MODE.HIDDEN;
    return CUSTOMER_CATALOG_MODE.LIVE;
}

/**
 * Customer catalogue visibility (Home, Catalog, Detail, place-on-behalf, loadFirestorePricingRows catalogVisibleOnly).
 * Live and enquiry SKUs are visible. Price does not matter.
 * Live box / hamper SKUs additionally require available Stock on that pricing sheet.
 */
export function isCatalogVisibleToCustomer(product) {
    const mode = resolveCustomerCatalogMode(product);
    if (mode === CUSTOMER_CATALOG_MODE.ENQUIRY) return true;
    if (mode !== CUSTOMER_CATALOG_MODE.LIVE) return false;
    if (isCustomerCatalogBoxProduct(product) && !isBoxStockAvailableForCustomer(product)) {
        return false;
    }
    return true;
}

/** True only for live (for-sale) SKUs. Enquiry SKUs never show a unit price. */
export function customerShouldSeePrice(product) {
    return resolveCustomerCatalogMode(product) === CUSTOMER_CATALOG_MODE.LIVE;
}

/**
 * Customer "in stock only" chip. Enquiry SKUs stay listed even when stock is 0
 * so a Christmas sold-out special is not filtered off the site.
 */
export function matchesCustomerInStockFilter(product, inStockOnly) {
    if (!inStockOnly) return true;
    if (resolveCustomerCatalogMode(product) === CUSTOMER_CATALOG_MODE.ENQUIRY) return true;
    return !!product?.inStock;
}

/** Filter raw Firestore rows or grid rows before customer display. */
export function filterCatalogVisibleProducts(products = []) {
    return (products || []).filter((p) => isCatalogVisibleToCustomer(p));
}
